import React, { useState } from 'react';
import { XMarkIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline';
import Button from '../ui/Button';
import { membershipsService } from '../../services/membershipsService';

interface Tier {
  id: string;
  name: string;
  amount: number;
  currency: string;
  description?: string;
  benefits?: string[];
}

interface ScheduledTierChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  currentTier: Tier;
  availableTiers: Tier[];
  onSuccess: () => void;
}

const ScheduledTierChangeModal: React.FC<ScheduledTierChangeModalProps> = ({
  isOpen,
  onClose,
  membershipId,
  currentTier,
  availableTiers,
  onSuccess,
}) => {
  const [selectedTierId, setSelectedTierId] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const scheduledFor = new Date(scheduledDate).toISOString();
      await membershipsService.scheduleTierChange(membershipId, {
        newTierId: selectedTierId,
        scheduledFor,
        reason: reason || undefined,
      });
      
      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to schedule tier change');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedTierId('');
    setScheduledDate('');
    setReason('');
    setError('');
    onClose();
  };

  const selectedTier = availableTiers.find(t => t.id === selectedTierId);
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateString = minDate.toISOString().split('T')[0];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Schedule Tier Change</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Current Tier Info */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Current Tier</h3>
            <p className="text-lg font-bold text-gray-900">{currentTier.name}</p>
            <p className="text-sm text-gray-600">
              ${currentTier.amount}/{currentTier.currency === 'USD' ? 'month' : currentTier.currency}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Tier
              </label>
              <select
                value={selectedTierId}
                onChange={(e) => setSelectedTierId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="">Choose a tier...</option>
                {availableTiers
                  .filter(t => t.id !== currentTier.id)
                  .map(tier => (
                    <option key={tier.id} value={tier.id}>
                      {tier.name} - ${tier.amount}/{tier.currency === 'USD' ? 'month' : tier.currency}
                    </option>
                  ))}
              </select>
            </div>

            {/* Selected Tier Benefits */}
            {selectedTier && selectedTier.benefits && selectedTier.benefits.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Benefits Included:</h4>
                <ul className="space-y-1">
                  {selectedTier.benefits.map((benefit, idx) => (
                    <li key={idx} className="text-sm text-gray-600 flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Schedule Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CalendarIcon className="w-4 h-4 inline mr-1" />
                Effective Date
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={minDateString}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                The tier change will take effect at midnight on this date
              </p>
            </div>

            {/* Reason (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (Optional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Why are you changing tiers?"
              />
            </div>

            {/* Price Comparison */}
            {selectedTier && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Price Change</h4>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Current: ${currentTier.amount}/month</span>
                  <span className="mx-2">→</span>
                  <span className="font-medium text-gray-900">
                    New: ${selectedTier.amount}/month
                  </span>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  {selectedTier.amount > currentTier.amount ? (
                    <>You'll be charged the difference on the effective date.</>
                  ) : selectedTier.amount < currentTier.amount ? (
                    <>You'll receive a prorated credit for the downgrade.</>
                  ) : (
                    <>Same price - tier benefits will change.</>
                  )}
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting || !selectedTierId || !scheduledDate}
                loading={isSubmitting}
              >
                Schedule Change
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduledTierChangeModal;
